import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "../lib/api";
import { formatCurrency } from "../lib/romEngine";
import { X, Sparkle, TrendUp, TrendDown, Minus, Warning, ChartBar } from "@phosphor-icons/react";

/* Right-hand slide-over showing the Schedule A-E variance between a job's ROM
 * and its recorded actual invoice, plus per-vendor drivers, invoice narration
 * flags, a deterministic summary and an optional AI insight. Reused on the Jobs
 * list and inside the Calculator. */
export function VarianceAnalysis({ jobId, open, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [aiBusy, setAiBusy] = useState(false);

    useEffect(() => {
        if (!open || !jobId) return;
        let alive = true;
        setLoading(true); setErr(""); setData(null);
        endpoints.jobVariance(jobId)
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => { if (alive) setErr(formatApiError(e)); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [open, jobId]);

    async function generateAI() {
        setAiBusy(true); setErr("");
        try {
            const { data: r } = await endpoints.jobVarianceAI(jobId);
            setData((d) => ({ ...d, ai_insight: r.ai_insight }));
        } catch (e) {
            setErr(formatApiError(e));
        }
        setAiBusy(false);
    }

    if (!open) return null;
    const a = data;
    return (
        <div className="fixed inset-0 z-[70] flex justify-end" data-testid="variance-slideover">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative w-full max-w-xl bg-[var(--rom-bg,#fff)] h-full overflow-y-auto shadow-2xl animate-[slideIn_.2s_ease]">
                <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-[var(--rom-navy)] text-white">
                    <div className="flex items-center gap-2">
                        <ChartBar size={20} weight="bold" className="text-[var(--rom-orange)]" />
                        <div>
                            <div className="font-display font-black text-lg leading-none">Variance Analysis</div>
                            {a?.ref && <div className="text-[11px] text-white/70 mt-1 font-mono">{a.ref} · {a.loss_type}</div>}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="variance-close"><X size={20} /></button>
                </div>

                <div className="p-6 space-y-5">
                    {loading && <div className="text-sm text-[var(--rom-text-2)] py-10 text-center">Loading analysis…</div>}
                    {err && <div className="text-sm text-[var(--rom-danger)] bg-red-50 border border-red-200 rounded p-3" data-testid="variance-error">{err}</div>}

                    {a && !a.available && (
                        <div className="text-sm text-[var(--rom-text-2)] bg-slate-50 border border-[var(--rom-border)] rounded p-4" data-testid="variance-none">
                            {a.summary}
                        </div>
                    )}

                    {a && a.available && (
                        <>
                            {/* Total hero */}
                            <div className="rounded-[8px] border border-[var(--rom-border)] p-5" data-testid="variance-hero">
                                <div className="flex items-end justify-between gap-4">
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-3)]">ROM budget</div>
                                        <div className="font-mono text-lg font-semibold">{formatCurrency(a.rom_total)}</div>
                                    </div>
                                    <DirArrow dir={a.total_direction} />
                                    <div className="text-right">
                                        <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-3)]">Actual invoice</div>
                                        <div className="font-mono text-lg font-semibold">{formatCurrency(a.actual_total)}</div>
                                    </div>
                                </div>
                                <div className="mt-4 flex items-center justify-center gap-3">
                                    <span className={`font-display font-black text-3xl ${pctTone(a.total_pct)}`} data-testid="variance-total-pct">
                                        {a.total_pct > 0 ? "+" : ""}{a.total_pct?.toFixed(1)}%
                                    </span>
                                    <span className="text-sm text-[var(--rom-text-2)]">({formatCurrency(a.total_delta)})</span>
                                    {a.engine_pct != null && (
                                        <span className="text-xs text-[var(--rom-text-3)] ml-2">vs engine {a.engine_pct > 0 ? "+" : ""}{a.engine_pct.toFixed(1)}%</span>
                                    )}
                                </div>
                            </div>

                            {/* Bucket table (Tier B) */}
                            {a.tier === "B" && a.buckets?.length > 0 ? (
                                <div data-testid="variance-buckets">
                                    <SectionTitle>Schedule A–E breakdown</SectionTitle>
                                    <table className="rom-table">
                                        <thead><tr><th>Schedule</th><th className="numeric">ROM</th><th className="numeric">Actual</th><th className="numeric">Δ</th><th className="numeric">%</th></tr></thead>
                                        <tbody>
                                            {a.buckets.map((b) => (
                                                <tr key={b.key} data-testid={`variance-bucket-${b.key}`}>
                                                    <td className="font-medium">{b.label}</td>
                                                    <td className="numeric font-mono">{formatCurrency(b.predicted)}</td>
                                                    <td className="numeric font-mono">{formatCurrency(b.actual)}</td>
                                                    <td className={`numeric font-mono ${pctTone(b.pct)}`}>{b.delta > 0 ? "+" : ""}{formatCurrency(b.delta)}</td>
                                                    <td className={`numeric font-mono ${pctTone(b.pct)}`}>{b.pct == null ? "—" : `${b.pct > 0 ? "+" : ""}${b.pct.toFixed(1)}%`}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-xs text-[var(--rom-text-2)] bg-amber-50 border border-amber-200 rounded p-3 flex gap-2" data-testid="variance-tier-a-note">
                                    <Warning size={16} weight="bold" className="text-amber-600 shrink-0 mt-0.5" />
                                    <span>Only a total (Tier A) was recorded for this job. Record a detailed Tier-B invoice to see the variance split across Schedule A–E.</span>
                                </div>
                            )}

                            {/* Vendor drivers */}
                            {a.vendor_drivers?.length > 0 && (
                                <div data-testid="variance-vendors">
                                    <SectionTitle>Per-vendor drivers</SectionTitle>
                                    <div className="space-y-1.5">
                                        {a.vendor_drivers.map((v, i) => (
                                            <div key={i} className="flex items-center justify-between gap-3 text-sm border border-[var(--rom-border)] rounded px-3 py-2" data-testid={`variance-vendor-${i}`}>
                                                <div className="min-w-0">
                                                    <div className="font-medium truncate">{v.item}{v.vendor ? ` · ${v.vendor}` : ""}</div>
                                                    <div className="text-[11px] text-[var(--rom-text-3)]">quoted {formatCurrency(v.quoted)} → actual {formatCurrency(v.actual)}</div>
                                                </div>
                                                <span className={`font-mono text-sm font-semibold shrink-0 ${pctTone(v.pct)}`}>
                                                    {v.delta > 0 ? "+" : ""}{formatCurrency(v.delta)}{v.pct != null ? ` (${v.pct > 0 ? "+" : ""}${v.pct.toFixed(0)}%)` : ""}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Invoice narration flags */}
                            {a.flags?.length > 0 && (
                                <div data-testid="variance-flags">
                                    <SectionTitle>Invoice narration / judgment calls</SectionTitle>
                                    <ul className="space-y-1 text-sm text-[var(--rom-text-2)] list-disc pl-5">
                                        {a.flags.map((f, i) => <li key={i}>{f}</li>)}
                                    </ul>
                                </div>
                            )}

                            {/* Deterministic summary */}
                            <div data-testid="variance-summary">
                                <SectionTitle>Summary</SectionTitle>
                                <p className="text-sm leading-relaxed text-[var(--rom-text)]">{a.summary}</p>
                            </div>

                            {/* AI insight */}
                            <div className="rounded-[8px] border-2 border-[var(--rom-orange)]/30 bg-[var(--rom-orange-soft)]/30 p-4" data-testid="variance-ai">
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2 font-semibold text-sm">
                                        <Sparkle size={16} weight="fill" className="text-[var(--rom-orange)]" /> AI insight
                                    </div>
                                    <button className="btn btn-primary btn-sm" onClick={generateAI} disabled={aiBusy} data-testid="variance-ai-generate">
                                        {aiBusy ? "Analysing…" : a.ai_insight ? "Regenerate" : "Generate AI insight"}
                                    </button>
                                </div>
                                {a.ai_insight ? (
                                    <>
                                        <p className="text-sm leading-relaxed whitespace-pre-line text-[var(--rom-text)]" data-testid="variance-ai-text">{a.ai_insight.text}</p>
                                        <div className="text-[10px] text-[var(--rom-text-3)] mt-2">Generated {new Date(a.ai_insight.generated_at).toLocaleString()}{a.ai_insight.generated_by ? ` · ${a.ai_insight.generated_by}` : ""}</div>
                                    </>
                                ) : (
                                    <p className="text-xs text-[var(--rom-text-2)]">Generate a narrative explanation of this variance using the invoice narration, schedule breakdown and per-vendor data. Requires an internet connection.</p>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
            <style>{`@keyframes slideIn{from{transform:translateX(24px);opacity:.6}to{transform:translateX(0);opacity:1}}`}</style>
        </div>
    );
}

function SectionTitle({ children }) {
    return <div className="text-[11px] uppercase tracking-[0.12em] font-bold text-[var(--rom-text-2)] mb-2">{children}</div>;
}

function DirArrow({ dir }) {
    if (dir === "over") return <TrendUp size={24} weight="bold" className="text-amber-600" />;
    if (dir === "under") return <TrendDown size={24} weight="bold" className="text-[var(--rom-green)]" />;
    return <Minus size={24} weight="bold" className="text-[var(--rom-text-3)]" />;
}

function pctTone(pct) {
    if (pct == null) return "text-[var(--rom-text-2)]";
    if (pct > 5) return "text-amber-700";
    if (pct < -5) return "text-[var(--rom-green)]";
    return "text-[var(--rom-text)]";
}
